import type { GroupBase, OptionsOrGroups } from 'react-select';

import { isNotDefined, isOfType } from '@enterprise/core';

const flatten = <Option, Group extends GroupBase<Option>>(
  options: OptionsOrGroups<Option, Group> | undefined,
  result: (Group | Option)[] = []
) => {
  if (isNotDefined(options)) {
    return [];
  }

  for (let i = 0, length = options.length; i < length; i++) {
    const value = options[i];

    if (isNotDefined(value)) {
      continue;
    }

    if (isOfType<GroupBase<Option>>(value, 'options')) {
      result.push(value);
      flatten(value.options, result);
    } else {
      result.push(value);
    }
  }

  return result;
};

/**
 * Flattens a nested array of options and groups into a single-level array.
 *
 * This utility function recursively traverses the provided options, which may contain
 * both individual options and groups of options, and returns a flat array containing
 * all options and groups in depth-first order.
 *
 * @template Option - The type of the option objects.
 * @template Group - The type of the group objects, extending GroupBase<Option>.
 * @param options - The array of options or groups to flatten. Can be undefined.
 * @param result - (Optional) An accumulator array used during recursion. Defaults to an empty array.
 * @returns An array containing all options and groups in a single, flat structure.
 */
export const flattenOptions = <Option, Group extends GroupBase<Option>>(options?: OptionsOrGroups<Option, Group>) => {
  return flatten(options);
};
